import { z } from 'zod';

/**
 * Stored registration acuity band. Derived by the interim proxy
 * (`interim-proxy-v1`) from the intake week and TAPS-2 answer, or mapped from a
 * migrated member's 1.0 card acuity text by the separate legacy import.
 */
export const ACUITY_BAND = ['none', 'low', 'moderate', 'high', 'unknown'] as const;
export type AcuityBand = (typeof ACUITY_BAND)[number];
export const acuityBandSchema = z.enum(ACUITY_BAND);

/** How the assessment row was produced — not a telemetry `source` tag dimension. */
export const ACUITY_ASSESSMENT_SOURCE = ['registration', 'legacy_import'] as const;
export type AcuityAssessmentSource = (typeof ACUITY_ASSESSMENT_SOURCE)[number];
export const acuityAssessmentSourceSchema = z.enum(ACUITY_ASSESSMENT_SOURCE);

/** Pinned rule version stamped on every stored row and wire projection. */
export const ACUITY_RULE_VERSION = 'interim-proxy-v1' as const;

/** Coded assessment outcomes — bounded tags only, never raw answers (ADR-069). */
export const ACUITY_ASSESSMENT_REASON_CODE = [
  'binge_day',
  'taps2_positive',
  'zero_drinks',
  'low_drinks',
  'no_baseline',
  'week_incomplete',
  'week_not_representative',
  'taps2_unanswered',
  'programme_not_alcohol',
  'derivation_failed',
] as const;
export type AcuityAssessmentReasonCode = (typeof ACUITY_ASSESSMENT_REASON_CODE)[number];
export const acuityAssessmentReasonCodeSchema = z.enum(ACUITY_ASSESSMENT_REASON_CODE);

/** @deprecated Use {@link ACUITY_ASSESSMENT_REASON_CODE} — unknown reasons are a subset. */
export const ACUITY_UNKNOWN_REASON = [
  'no_baseline',
  'week_incomplete',
  'week_not_representative',
  'taps2_unanswered',
  'programme_not_alcohol',
] as const;
export type AcuityUnknownReason = (typeof ACUITY_UNKNOWN_REASON)[number];
export const acuityUnknownReasonSchema = z.enum(ACUITY_UNKNOWN_REASON);

/** Staff-facing projection of the latest registration acuity for the main programme. */
export const registrationAcuitySchema = z.object({
  band: acuityBandSchema,
  source: acuityAssessmentSourceSchema,
  ruleVersion: z.string().min(1).max(64),
  assessedAt: z.string().datetime({ offset: true }),
});
export type RegistrationAcuity = z.infer<typeof registrationAcuitySchema>;
